import React, { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Collapse,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Diamond as DiamondIcon,
} from '@mui/icons-material';
import { useDebounce } from 'use-debounce';
import SubscriptionStatsCards from './components/SubscriptionStatsCards';
import HostsTable from './components/HostsTable';
import PricingConfigCard from './components/PricingConfigCard';
import FleetCriteriaCard from './components/FleetCriteriaCard';
import GrantSubscriptionDialog from './components/GrantSubscriptionDialog';
import UpdatePricingDialog from './components/UpdatePricingDialog';
import UpdateCriteriaDialog from './components/UpdateCriteriaDialog';
import { useFleetSubscriptions, useExportSubscriptions } from './hooks/useFleetSubscriptions';
import type {
  HostSubscriptionInfo,
  TabValue,
  SubscriptionFilters,
  HostTypeFilter,
  SubscriptionStatus,
  QualificationFilter,
  SortField,
  SortOrder,
} from './types/subscription.types';

const FleetSubscriptionManagement: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<TabValue>('all');

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch] = useDebounce(searchQuery, 300);
  const [hostTypeFilter, setHostTypeFilter] = useState<HostTypeFilter>('all');
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] =
    useState<SubscriptionStatus>('all');
  const [qualificationFilter, setQualificationFilter] = useState<QualificationFilter>('all');
  const [expiringSoonFilter, setExpiringSoonFilter] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Sorting states
  const [sortBy, setSortBy] = useState<SortField>('name');
  const [sortOrder] = useState<SortOrder>('asc');

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Selection states
  const [selectedHosts, setSelectedHosts] = useState<number[]>([]);

  // Dialog states
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [updatePricingDialogOpen, setUpdatePricingDialogOpen] = useState(false);
  const [updateCriteriaDialogOpen, setUpdateCriteriaDialogOpen] = useState(false);
  const [selectedHost, setSelectedHost] = useState<HostSubscriptionInfo | null>(null);

  // Build filters object based on active tab
  const filters = useMemo<SubscriptionFilters>(() => {
    const baseFilters: SubscriptionFilters = {
      search: debouncedSearch || undefined,
      host_type: hostTypeFilter !== 'all' ? hostTypeFilter : undefined,
      qualification: qualificationFilter !== 'all' ? qualificationFilter : undefined,
      expiring_soon: expiringSoonFilter || undefined,
      page: page + 1, // API uses 1-based pagination
      limit: rowsPerPage,
      sort_by: sortBy,
      sort_order: sortOrder,
    };

    // Apply tab-specific filters
    if (activeTab === 'active') {
      baseFilters.subscription_status = 'active';
    } else if (activeTab === 'expired') {
      baseFilters.subscription_status = 'expired';
    } else {
      baseFilters.subscription_status =
        subscriptionStatusFilter !== 'all' ? subscriptionStatusFilter : undefined;
    }

    return baseFilters;
  }, [
    debouncedSearch,
    hostTypeFilter,
    subscriptionStatusFilter,
    qualificationFilter,
    expiringSoonFilter,
    page,
    rowsPerPage,
    sortBy,
    sortOrder,
    activeTab,
  ]);

  // Fetch data
  const { data, isLoading, error, refetch } = useFleetSubscriptions(filters);
  const exportMutation = useExportSubscriptions();

  // Handlers
  const handleTabChange = (_: React.SyntheticEvent, newValue: TabValue) => {
    setActiveTab(newValue);
    setPage(0); // Reset to first page when changing tabs
    setSelectedHosts([]); // Clear selections
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setSelectedHosts([]);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setSelectedHosts([]);
  };

  const handleSelectHost = (hostId: number) => {
    setSelectedHosts((prev) =>
      prev.includes(hostId) ? prev.filter((id) => id !== hostId) : [...prev, hostId]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedHosts(data?.hosts.map((host) => host.user_id) || []);
    } else {
      setSelectedHosts([]);
    }
  };

  const handleGrantSubscription = (host: HostSubscriptionInfo) => {
    setSelectedHost(host);
    setGrantDialogOpen(true);
  };

  const handleViewDetails = (host: HostSubscriptionInfo) => {
    // TODO: Implement host details drawer
  };

  const handleViewHistory = (host: HostSubscriptionInfo) => {
    // TODO: Implement subscription history view
  };

  const handleExport = () => {
    exportMutation.mutate(filters);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setHostTypeFilter('all');
    setSubscriptionStatusFilter('all');
    setQualificationFilter('all');
    setExpiringSoonFilter(false);
    setPage(0);
  };

  const hasActiveFilters =
    searchQuery ||
    hostTypeFilter !== 'all' ||
    subscriptionStatusFilter !== 'all' ||
    qualificationFilter !== 'all' ||
    expiringSoonFilter;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <DiamondIcon sx={{ fontSize: 32, color: '#9333ea' }} />
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Fleet Subscription Management
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Manage premium fleet subscriptions, pricing, and host qualifications
        </Typography>
      </Box>

      {/* Stats Cards */}
      <SubscriptionStatsCards summary={data?.summary} isLoading={isLoading} />

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab
            label="All Hosts"
            value="all"
            icon={<Chip label={data?.summary?.total_hosts || 0} size="small" />}
            iconPosition="end"
          />
          <Tab
            label="Active Subscriptions"
            value="active"
            icon={<Chip label={data?.summary?.active_subscriptions || 0} size="small" color="success" />}
            iconPosition="end"
          />
          <Tab
            label="Expired Subscriptions"
            value="expired"
            icon={<Chip label={data?.summary?.expired_subscriptions || 0} size="small" color="error" />}
            iconPosition="end"
          />
          <Tab label="Pricing Configuration" value="pricing" />
        </Tabs>
      </Paper>

      {/* Pricing Tab */}
      {activeTab === 'pricing' ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <PricingConfigCard onUpdatePricing={() => setUpdatePricingDialogOpen(true)} />
          <FleetCriteriaCard onUpdateCriteria={() => setUpdateCriteriaDialogOpen(true)} />
        </Box>
      ) : (
        <>
          {/* Filters & Search */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              {/* Search */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search hosts..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: searchQuery && (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchQuery('')}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {/* Quick Filters for All Hosts tab */}
              {activeTab === 'all' && (
                <>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={subscriptionStatusFilter}
                        label="Status"
                        onChange={(e) => {
                          setSubscriptionStatusFilter(e.target.value as SubscriptionStatus);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="expired">Expired</MenuItem>
                        <MenuItem value="none">No Subscription</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Host Type</InputLabel>
                      <Select
                        value={hostTypeFilter}
                        label="Host Type"
                        onChange={(e) => {
                          setHostTypeFilter(e.target.value as HostTypeFilter);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="fleet_manager">Fleet Manager</MenuItem>
                        <MenuItem value="individual">Individual</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Sort By</InputLabel>
                      <Select
                        value={sortBy}
                        label="Sort By"
                        onChange={(e) => {
                          setSortBy(e.target.value as SortField);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="name">Name</MenuItem>
                        <MenuItem value="vehicles">Vehicles</MenuItem>
                        <MenuItem value="expiry">Expiry Date</MenuItem>
                        <MenuItem value="created_at">Join Date</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )}

              {/* Actions */}
              <Grid item xs={12} md={activeTab === 'all' ? 2 : 8} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                <Tooltip title="Advanced Filters">
                  <IconButton
                    onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                    color={showAdvancedFilters ? 'primary' : 'default'}
                  >
                    <FilterListIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Refresh">
                  <IconButton onClick={() => refetch()}>
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExport}
                  disabled={exportMutation.isPending}
                  size="small"
                >
                  Export
                </Button>
              </Grid>
            </Grid>

            {/* Advanced Filters */}
            <Collapse in={showAdvancedFilters}>
              <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Qualification</InputLabel>
                      <Select
                        value={qualificationFilter}
                        label="Qualification"
                        onChange={(e) => {
                          setQualificationFilter(e.target.value as QualificationFilter);
                          setPage(0);
                        }}
                      >
                        <MenuItem value="all">All</MenuItem>
                        <MenuItem value="vehicles">5+ Vehicles</MenuItem>
                        <MenuItem value="premium">Premium Only</MenuItem>
                        <MenuItem value="both">Both</MenuItem>
                        <MenuItem value="none">Not Qualified</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={3}>
                    <Button
                      variant={expiringSoonFilter ? 'contained' : 'outlined'}
                      color={expiringSoonFilter ? 'warning' : 'inherit'}
                      onClick={() => {
                        setExpiringSoonFilter(!expiringSoonFilter);
                        setPage(0);
                      }}
                      fullWidth
                    >
                      Expiring Soon (&lt; 30 days)
                    </Button>
                  </Grid>

                  <Grid item xs={12} md={6} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
                    {hasActiveFilters && (
                      <Button
                        variant="text"
                        size="small"
                        onClick={handleClearFilters}
                        startIcon={<CloseIcon />}
                      >
                        Clear All Filters
                      </Button>
                    )}
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Paper>

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {searchQuery && (
                <Chip
                  label={`Search: "${searchQuery}"`}
                  onDelete={() => setSearchQuery('')}
                  size="small"
                />
              )}
              {expiringSoonFilter && (
                <Chip
                  label="Expiring Soon"
                  onDelete={() => setExpiringSoonFilter(false)}
                  color="warning"
                  size="small"
                />
              )}
              {qualificationFilter !== 'all' && (
                <Chip
                  label={`Qualification: ${qualificationFilter}`}
                  onDelete={() => setQualificationFilter('all')}
                  size="small"
                />
              )}
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Failed to load subscriptions
              </Typography>
              <Typography variant="body2">{error.message}</Typography>
              <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                Make sure the backend API is running and the endpoints are available at: /api/v1/admin/fleet/subscriptions
              </Typography>
            </Alert>
          )}

          {/* Selected Hosts Actions */}
          {selectedHosts.length > 0 && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              action={
                <Button size="small" onClick={() => setSelectedHosts([])}>
                  Clear
                </Button>
              }
            >
              {selectedHosts.length} host{selectedHosts.length > 1 ? 's' : ''} selected
            </Alert>
          )}

          {/* Hosts Table */}
          <HostsTable
            hosts={data?.hosts || []}
            isLoading={isLoading}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={data?.pagination?.total || 0}
            selectedHosts={selectedHosts}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSelectHost={handleSelectHost}
            onSelectAll={handleSelectAll}
            onGrantSubscription={handleGrantSubscription}
            onViewDetails={handleViewDetails}
            onViewHistory={handleViewHistory}
          />
        </>
      )}

      {/* Dialogs */}
      <GrantSubscriptionDialog
        open={grantDialogOpen}
        host={selectedHost}
        onClose={() => {
          setGrantDialogOpen(false);
          setSelectedHost(null);
        }}
        onSuccess={() => refetch()}
      />

      <UpdatePricingDialog
        open={updatePricingDialogOpen}
        onClose={() => setUpdatePricingDialogOpen(false)}
        onSuccess={() => refetch()}
      />

      <UpdateCriteriaDialog
        open={updateCriteriaDialogOpen}
        onClose={() => setUpdateCriteriaDialogOpen(false)}
        onSuccess={() => refetch()}
      />
    </Container>
  );
};

export default FleetSubscriptionManagement;
